/** @format */

import {
	Aggregations,
	Brand,
	CreateQuestionInput,
	createQuestionSchema,
	createGroupedRootQuestionSchema,
	Paper,
	QUESTION_ASSET_WIDTHS,
	QuestionContentBlock,
	QuestionStatus,
	isPublishByStager,
	isSubjectMatterExpert,
} from "@question-bank/shared";
import { useState, useEffect, useMemo, useRef, type FormEvent } from "react";
import { UILanguage } from "./LanguageToggle";
import { formatSeason } from "../utils/helpers";
import { useForm, useFieldArray, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { ImageUpload } from "./ImageUpload";
import { MathTextField } from "./MathTextField";
import { listPapers } from "../api/papers";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { notify } from "./Toast/toast";
import { Locale } from "../features/i18n/init";

interface QuestionFormProps {
	defaultLanguage: UILanguage;
	aggregations: Aggregations;
	onBrandChange?: (brand: Brand) => void;
	initialValues?: Partial<CreateQuestionInput>;
	onSubmit: (data: CreateQuestionInput) => Promise<void>;
	isDisabled?: boolean;
	errorMessage?: string;
	successMessage?: string;
	onSuccess?: () => void;
	onCancel?: () => void;
	/** Translation key for the secondary save button. Defaults to the
	 * "Save as Draft" label; edit flows pass a plain "Save" instead. */
	draftButtonLabelKey?: string;
	/** Whether the question being edited already has child parts. A root with
	 * parts derives its marks from them, so the max marks field is hidden. */
	hasParts?: boolean;
}

const labelStyle = "text-xl font-bold";
const divStyle = "flex flex-col gap-1 mt-4";
// Grid cell: no top margin (the grid gap handles spacing) so fields align in rows.
const fieldStyle = "flex flex-col gap-1";
const inputStyle =
	"border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-(--admin-color-primary-strong) disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed";

const RequiredMark = () => (
	<span className="text-red-500" aria-hidden="true">
		{" *"}
	</span>
);

const Spinner = () => (
	<svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
		<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
		<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
	</svg>
);

/** Drop blocks with no content: empty text blocks and image blocks with no image. */
const cleanUpBlocks = (blocks?: QuestionContentBlock[]): QuestionContentBlock[] =>
	(blocks ?? []).filter((block) =>
		block.type === "answerLines" ? block.lines > 0 : Boolean(block.en?.trim() || block.cy?.trim()),
	);

export const QuestionForm = ({
	defaultLanguage,
	onBrandChange,
	initialValues,
	onSubmit,
	isDisabled,
	errorMessage,
	successMessage,
	onSuccess,
	onCancel,
	draftButtonLabelKey = "questionForm.saveDraftButton",
	hasParts,
}: QuestionFormProps) => {
	// A grouped root (a root that already has parts) holds its content in those
	// parts, so question content is not required when editing it. The decision
	// depends on watched form state, so it's tracked via a ref the resolver
	// reads at validation time to pick the matching schema.
	const isGroupedRootRef = useRef(false);
	const resolver = useMemo<Resolver<CreateQuestionInput>>(
		() => (values, context, options) => {
			const schema = isGroupedRootRef.current ? createGroupedRootQuestionSchema : createQuestionSchema;
			return (zodResolver(schema) as Resolver<CreateQuestionInput>)(values, context, options);
		},
		[],
	);

	// Build the form's default values from the record's ordered content blocks.
	// Paper-level facts (subject, level, series, tier, date) are no longer part
	// of a question (QB2-223); they belong to the Paper and are not editable here.
	const buildDefaults = (init?: Partial<CreateQuestionInput>): Partial<CreateQuestionInput> => ({
		language: defaultLanguage,
		brand: "wjec" as Brand,
		...init,
		contentBlocks: init?.contentBlocks ?? [],
	});

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isDirty },
		setValue,
		getValues,
		watch,
		control,
	} = useForm<CreateQuestionInput>({
		resolver,
		mode: "onSubmit",
		defaultValues: buildDefaults(initialValues),
	});

	const {
		fields: contentBlockFields,
		append: appendBlock,
		remove: removeBlock,
		move: moveBlock,
	} = useFieldArray({ control, name: "contentBlocks" });

	useEffect(() => {
		if (initialValues) {
			reset(buildDefaults(initialValues));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, defaultLanguage, reset]);

	useEffect(() => {
		setValue("language", defaultLanguage);
	}, [defaultLanguage, setValue]);

	const selectedBrand = watch("brand");
	useEffect(() => {
		onBrandChange?.(selectedBrand);
	}, [selectedBrand, onBrandChange]);

	// Load the brand's papers so a question can be attached to one. A question
	// inherits its paper-level facts (subject/level/tier/series/date) from the
	// paper it references (QB2-223), so the paper is a required selection.
	const [papers, setPapers] = useState<Paper[]>([]);
	useEffect(() => {
		if (!selectedBrand) {
			return;
		}
		let isCancelled = false;
		listPapers(selectedBrand)
			.then((list) => {
				if (!isCancelled) {
					setPapers(list);
				}
			})
			.catch(() => {
				if (!isCancelled) {
					setPapers([]);
				}
			});
		return () => {
			isCancelled = true;
		};
	}, [selectedBrand]);

	// The paper <select> is uncontrolled (react-hook-form register). On an edit,
	// the form's paperId default is set before the papers list loads, so the
	// native select has no matching <option> yet and falls back to the
	// placeholder. Once the papers arrive, re-assert the value so the select
	// shows the question's paper. Only do this when the value matches a loaded
	// option, so we never clobber a user's in-progress choice.
	useEffect(() => {
		if (papers.length === 0) {
			return;
		}
		const currentPaperId = getValues("paperId");
		if (currentPaperId && papers.some((paper) => paper.id === currentPaperId)) {
			setValue("paperId", currentPaperId);
		}
	}, [papers, getValues, setValue]);

	const isWelshRequired = selectedBrand === "wjec";

	// Asset keys for mark scheme images already saved on the question. Watched so
	// that the upload widgets show the existing image (and its remove button)
	// when editing. Question-body images are managed through the content blocks.
	const existingMarkSchemeImageEn = watch("en.markSchemeImage");
	const existingMarkSchemeImageCy = watch("cy.markSchemeImage");

	// Root questions have no ribbon parts (e.g. "1"); parts do (e.g. "1(a)").
	// A "grouped parent" is any question that already has its own ribbon parts —
	// both a top-level root (e.g. `1` with `1(a)`, `1(b)`) and an intermediate
	// part that has sub-parts (e.g. `1(a)` with `1(a)(i)`, `1(a)(ii)`). A grouped
	// parent derives its marks, content and mark schemes from those parts, so
	// max marks is hidden and content/mark schemes are optional. A leaf question
	// (a standalone root or a part with no sub-parts) still needs its own.
	const isGroupedParent = Boolean(hasParts);
	const canShowMaxMarks = !isGroupedParent;

	// A grouped parent holds its content in its parts, so question content is not
	// required for it. Keep the resolver's ref in sync so validation matches.
	isGroupedRootRef.current = isGroupedParent;
	const isContentRequired = !isGroupedParent;

	const [isSubmitted, setIsSubmitted] = useState(false);
	// Which save is in flight (if any), so the matching button can show progress
	// and both buttons can be disabled while the request runs.
	const [savingStatus, setSavingStatus] = useState<QuestionStatus | null>(null);

	const navigate = useNavigate();

	const { t } = useTranslation();
	const { user } = useAuth();
	const isExpert = isSubjectMatterExpert(user?.group);
	// The form type omits provenance. An edit still passes the loaded question.
	const stagedQuestion = (initialValues ?? {}) as { reviewProvenance?: { staged?: { userId: string } } };
	const isSameUserPublish =
		isPublishByStager(stagedQuestion, user?.sub) && initialValues?.status === "draft" && isExpert;
	const { locale } = useParams<{ locale: Locale }>();
	const aoLabelKeys = {
		ao1: "questionForm.assessmentObjectives.ao1Label",
		ao2: "questionForm.assessmentObjectives.ao2Label",
		ao3: "questionForm.assessmentObjectives.ao3Label",
	} as const;

	const submitQuestion = async (data: CreateQuestionInput) => {
		// Blocks are the source of truth for the question body. Drop empty blocks
		// an author left behind before submitting.
		const cleaned: CreateQuestionInput = {
			...data,
			contentBlocks: cleanUpBlocks(data.contentBlocks),
		};
		setSavingStatus(cleaned.status);
		try {
			await onSubmit(cleaned);
			notify("success", successMessage ?? t("questionForm.successDefault"));
			if (onSuccess) {
				onSuccess();
				return;
			}
			setIsSubmitted(true);
		} catch {
			notify("error", errorMessage ?? t("questionForm.errorDefault"));
		} finally {
			setSavingStatus(null);
		}
	};

	// A question can be saved as a draft with whatever the author has entered so
	// far — no client-side validation gate. Publish-readiness is enforced when
	// they choose to publish (and again on the server).
	const saveWithStatus = (status: QuestionStatus) => submitQuestion({ ...getValues(), status });

	// Publishing requires a fully valid question, so it runs the full schema
	// validation (surfacing inline errors) before submitting.
	const publish = handleSubmit((data) => submitQuestion({ ...data, status: "published" }));
	const stage = handleSubmit((data) => submitQuestion({ ...data, status: "staged" }));

	// Editing a question that is already published saves leniently and lets the
	// server keep it published when still valid, or demote it to draft if the
	// edit broke a publish requirement.
	const initialStatus = initialValues?.status;
	const isEditingPublished = initialStatus === "published";
	const isStaged = initialStatus === "staged";
	const shouldSaveCurrentStatus = isEditingPublished || isStaged;

	const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
		// A staged edit stays staged. Publish of a staged question is the
		// status control on the question page, and only an expert may do it.
		// Save as draft would be a rejection, which needs a reason there.
		if (shouldSaveCurrentStatus) {
			event.preventDefault();
			void saveWithStatus(isStaged ? "staged" : "published");
			return;
		}
		// A non-expert cannot publish. The validated submit stages the question.
		if (!isExpert) {
			void stage(event);
			return;
		}
		if (isSameUserPublish) {
			return;
		}
		void publish(event);
	};

	const handleCancel = () => {
		if (onCancel) {
			if (!isDirty || globalThis.window.confirm(t("questionForm.unsavedChanges"))) {
				onCancel();
			}
			return;
		}
		if (isDirty) {
			if (globalThis.window.confirm(t("questionForm.unsavedChanges"))) {
				navigate(`/${locale}/admin/questions`);
			}
		} else {
			navigate(`/${locale}/admin/questions`);
		}
	};

	return (
		<form className="px-8 w-full" onSubmit={handleFormSubmit}>
			{isSubmitted && (
				<div>
					<div className="flex gap-4 justify-center mt-4 mb-4">
						<button
							type="button"
							onClick={() => navigate("/admin/questions")}
							className="bg-gray-200 text-gray-700 w-40 h-8 rounded-md font-bold cursor-pointer"
						>
							{t("questionForm.goToQuestions")}
						</button>
						<button
							type="button"
							onClick={() => {
								setIsSubmitted(false);
								reset();
							}}
							className="bg-(--admin-color-primary-strong) text-white w-60 h-8 rounded-md font-bold cursor-pointer"
						>
							{t("questionForm.createAnother")}
						</button>
					</div>
				</div>
			)}

			<div>
				<p className="mb-4 text-sm text-gray-600">
					<RequiredMark />
					{` ${t("questionForm.requiredNote")}`}
				</p>
				<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
					<div className={fieldStyle}>
						<label htmlFor="paper-id" className={labelStyle}>
							{t("questionForm.paperLabel")}
							<RequiredMark />
						</label>
						<select
							id="paper-id"
							disabled={isDisabled}
							className={`${inputStyle}  w-full h-10`}
							aria-invalid={!!errors.paperId}
							aria-describedby={errors.paperId ? "paper-id-error" : "paper-id-hint"}
							{...register("paperId", {
								onChange: (event) => {
									// Keep paperCode in step with the chosen paper (derived, read-only).
									const chosen = papers.find((paper) => paper.id === event.target.value);
									setValue("paperCode", chosen?.paperCode ?? "");
								},
							})}
						>
							<option value="">{t("questionForm.paperPlaceholder")}</option>
							{papers.map((paper) => (
								<option key={paper.id} value={paper.id}>
									{paper.paperCode} — {Array.isArray(paper.subject) ? paper.subject.join(", ") : paper.subject} (
									{formatSeason(paper.series.season)} {paper.series.year})
								</option>
							))}
						</select>
						<p id="paper-id-hint" className="text-sm text-gray-600 mt-1">
							{t("questionForm.paperHint")}
						</p>
						{errors.paperId && (
							<p id="paper-id-error" className="text-red-500 text-sm mt-1">
								{errors.paperId.message}
							</p>
						)}
					</div>

					<div className={fieldStyle}>
						<label htmlFor="paper-code" className={labelStyle}>
							{t("questionForm.paperCodeLabel")}
						</label>
						<input
							id="paper-code"
							type="text"
							readOnly
							disabled={isDisabled}
							className={`${inputStyle}  w-full h-10 bg-gray-100`}
							aria-invalid={!!errors.paperCode}
							aria-describedby={errors.paperCode ? "paper-code-error" : undefined}
							{...register("paperCode")}
						/>
						{errors.paperCode && (
							<p id="paper-code-error" className="text-red-500 text-sm mt-1">
								{errors.paperCode.message}
							</p>
						)}
					</div>

					<div className={fieldStyle}>
						<label htmlFor="question-number" className={labelStyle}>
							{t("questionForm.questionNumberLabel")}
							<RequiredMark />
						</label>
						<input
							id="question-number"
							type="text"
							disabled={isDisabled}
							maxLength={30}
							placeholder={t("questionForm.questionNumberPlaceholder")}
							aria-describedby={
								errors.questionNumber ? "question-number-error question-number-hint" : "question-number-hint"
							}
							aria-invalid={!!errors.questionNumber}
							className={`${inputStyle}  w-full h-10`}
							{...register("questionNumber")}
						/>
						<p id="question-number-hint" className="text-sm text-gray-600 mt-1">
							{t("questionForm.questionNumberHint")}
						</p>
						{errors.questionNumber && (
							<p id="question-number-error" className="text-red-500 text-sm mt-1">
								{errors.questionNumber.message}
							</p>
						)}
					</div>

					{/* <div className={fieldStyle}>
						<label htmlFor="brand" className={labelStyle}>
							{t("questionForm.brandLabel")}
							<RequiredMark />
						</label>
						<select id="brand" disabled={isDisabled} className={`${inputStyle}  w-full h-10`} {...register("brand")}>
							<option value="wjec">{t("questionForm.brandWjec")}</option>
							<option value="eduqas">{t("questionForm.brandEduqas")}</option>
						</select>
					</div> */}

					{/* <div className={fieldStyle}>
						<label htmlFor="language" className={labelStyle}>
							{t("questionForm.languageLabel")}
							<RequiredMark />
						</label>
						<select
							id="language"
							disabled={isDisabled}
							className={`${inputStyle}  w-full h-10`}
							{...register("language")}
						>
							<option value="en">{t("questionForm.languageEnglish")}</option>
							<option value="cy">{t("questionForm.languageWelsh")}</option>
						</select>
					</div> */}

					{canShowMaxMarks && (
						<div className={fieldStyle}>
							{/* Max marks stays editable even when the identifying fields are locked. */}
							<label htmlFor="marks" className={labelStyle}>
								{t("questionForm.marksLabel")}
								<RequiredMark />
							</label>
							<input
								id="marks"
								type="number"
								className={`${inputStyle} w-full h-10`}
								min={1}
								aria-invalid={!!errors.maxMarks}
								aria-describedby={errors.maxMarks ? "marks-error" : undefined}
								{...register("maxMarks", { valueAsNumber: true })}
							/>
							{errors.maxMarks && (
								<p id="marks-error" className="text-red-500 text-sm mt-1">
									{errors.maxMarks.message}
								</p>
							)}
						</div>
					)}

					<div className={fieldStyle}>
						<label htmlFor="answer-lines" className={labelStyle}>
							{t("questionForm.answerLinesLabel")}
						</label>
						<input
							id="answer-lines"
							type="number"
							title={t("questionForm.answerLinesHint")}
							className={`${inputStyle} w-full h-10`}
							min={0}
							placeholder={t("questionForm.answerLinesPlaceholder")}
							aria-invalid={!!errors.answerLines}
							aria-describedby={errors.answerLines ? "answer-lines-error" : undefined}
							{...register("answerLines", {
								setValueAs: (value) => (value === "" || value === null ? undefined : Number(value)),
							})}
						/>
						{errors.answerLines && (
							<p id="answer-lines-error" className="text-red-500 text-sm mt-1">
								{errors.answerLines.message}
							</p>
						)}
					</div>
				</div>

				<fieldset className="mt-6 border border-gray-200 rounded-md p-4">
					<legend className={labelStyle}>
						{t("questionForm.questionContentLegend")}
						{isContentRequired && <RequiredMark />}
					</legend>
					<p className="text-sm text-gray-600 mb-3">{t("questionForm.contentBlocksDescription")}</p>

					{typeof errors.contentBlocks?.message === "string" && (
						<p className="text-red-500 text-sm mb-3">{errors.contentBlocks.message}</p>
					)}

					<div className="flex flex-col gap-4">
						{contentBlockFields.map((field, index) => {
							const blockType = field.type;
							const blockLabel =
								blockType === "asset"
									? t("questionForm.imageBlockLabel")
									: blockType === "answerLines"
										? t("questionForm.answerLinesBlockLabel")
										: t("questionForm.textBlockLabel");
							return (
								<div key={field.id} className="rounded-md border border-gray-200 p-3">
									<div className="mb-2 flex items-center justify-between gap-2">
										<span className="font-semibold">{`${index + 1}. ${blockLabel}`}</span>
										<div className="flex gap-2">
											<button
												type="button"
												aria-label={t("questionForm.moveBlockUp")}
												disabled={index === 0}
												onClick={() => moveBlock(index, index - 1)}
												className="rounded-md border border-gray-300 px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-40"
											>
												↑
											</button>
											<button
												type="button"
												aria-label={t("questionForm.moveBlockDown")}
												disabled={index === contentBlockFields.length - 1}
												onClick={() => moveBlock(index, index + 1)}
												className="rounded-md border border-gray-300 px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-40"
											>
												↓
											</button>
											<button
												type="button"
												onClick={() => removeBlock(index)}
												className="rounded-md border border-red-300 px-2 py-1 text-sm font-semibold text-red-600"
											>
												{t("questionForm.removeBlock")}
											</button>
										</div>
									</div>

									{blockType === "asset" ? (
										<div className="flex flex-col gap-4">
											<div className="grid grid-cols-2 gap-6">
												<ImageUpload
													label={t("questionForm.imageLanguageEnglish")}
													brand={selectedBrand}
													imageInputId={`content-block-asset-${index}-en`}
													existingAssetKey={watch(`contentBlocks.${index}.en`) || undefined}
													onImageUpload={(assetKey) => setValue(`contentBlocks.${index}.en`, assetKey)}
													onImageRemove={() => setValue(`contentBlocks.${index}.en`, "")}
												/>
												<ImageUpload
													label={t("questionForm.imageLanguageWelsh")}
													brand={selectedBrand}
													imageInputId={`content-block-asset-${index}-cy`}
													existingAssetKey={watch(`contentBlocks.${index}.cy`) || undefined}
													onImageUpload={(assetKey) => setValue(`contentBlocks.${index}.cy`, assetKey)}
													onImageRemove={() => setValue(`contentBlocks.${index}.cy`, "")}
												/>
											</div>
											<div className={fieldStyle}>
												<label htmlFor={`content-block-${index}-width`} className="font-semibold">
													{t("questionForm.imageBlockWidthLabel")}
												</label>
												<select
													id={`content-block-${index}-width`}
													className={`${inputStyle} w-48 h-10`}
													{...register(`contentBlocks.${index}.width`, {
														setValueAs: (value) => (value === "" || value === null ? undefined : Number(value)),
													})}
												>
													<option value="">{t("questionForm.imageBlockWidthDefaultOption")}</option>
													{QUESTION_ASSET_WIDTHS.map((width) => (
														<option key={width} value={width}>{`${width}%`}</option>
													))}
												</select>
												<p className="text-sm text-gray-600">{t("questionForm.imageBlockWidthHint")}</p>
											</div>
										</div>
									) : blockType === "answerLines" ? (
										<div className={fieldStyle}>
											<label htmlFor={`content-block-${index}-lines`} className="font-semibold">
												{t("questionForm.answerLinesBlockFieldLabel")}
											</label>
											<input
												id={`content-block-${index}-lines`}
												type="number"
												min={1}
												max={60}
												className={`${inputStyle} w-32 h-10`}
												{...register(`contentBlocks.${index}.lines`, { valueAsNumber: true })}
											/>
										</div>
									) : (
										<div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2">
											<MathTextField
												id={`content-block-${index}-en`}
												label={t("questionForm.questionContentEnLabel")}
												value={watch(`contentBlocks.${index}.en`) ?? ""}
												registration={register(`contentBlocks.${index}.en`)}
												textareaClassName={inputStyle}
											/>
											<MathTextField
												id={`content-block-${index}-cy`}
												label={t("questionForm.questionContentCyLabel")}
												value={watch(`contentBlocks.${index}.cy`) ?? ""}
												registration={register(`contentBlocks.${index}.cy`)}
												textareaClassName={inputStyle}
											/>
										</div>
									)}
								</div>
							);
						})}
					</div>

					<div className="mt-3 flex flex-wrap gap-3">
						<button
							type="button"
							onClick={() => appendBlock({ type: "text", en: "", cy: "" })}
							className="rounded-md border border-(--admin-color-primary-strong) px-3 py-1.5 text-sm font-bold text-(--admin-color-primary-strong)"
						>
							{t("questionForm.addTextBlock")}
						</button>
						<button
							type="button"
							onClick={() => appendBlock({ type: "asset", en: "", cy: "" })}
							className="rounded-md border border-(--admin-color-primary-strong) px-3 py-1.5 text-sm font-bold text-(--admin-color-primary-strong)"
						>
							{t("questionForm.addImageBlock")}
						</button>
						<button
							type="button"
							onClick={() => appendBlock({ type: "answerLines", lines: 4 })}
							className="rounded-md border border-(--admin-color-primary-strong) px-3 py-1.5 text-sm font-bold text-(--admin-color-primary-strong)"
						>
							{t("questionForm.addAnswerLinesBlock")}
						</button>
					</div>
				</fieldset>

				<fieldset className="mt-6 border border-gray-200 rounded-md p-4">
					<legend className={labelStyle}>{t("questionForm.specificationLegend")}</legend>
					<p className="text-sm text-gray-600 mb-2">{t("questionForm.specificationDescription")}</p>

					<div className={divStyle}>
						<label htmlFor="specification-theme" className="font-semibold">
							{t("questionForm.specificationThemeLabel")}
						</label>
						<input
							id="specification-theme"
							type="text"
							disabled={isDisabled}
							maxLength={200}
							aria-invalid={!!errors.specification?.theme}
							aria-describedby={errors.specification?.theme ? "specification-theme-error" : undefined}
							className={`${inputStyle}  w-full h-10`}
							{...register("specification.theme", { setValueAs: (val) => (val === "" ? undefined : val) })}
						/>
						{errors.specification?.theme && (
							<p id="specification-theme-error" className="text-red-500 text-sm mt-1">
								{errors.specification.theme.message}
							</p>
						)}
					</div>

					<div className={divStyle}>
						<label htmlFor="specification-topic" className="font-semibold">
							{t("questionForm.specificationTopicLabel")}
						</label>
						<input
							id="specification-topic"
							type="text"
							disabled={isDisabled}
							maxLength={200}
							aria-invalid={!!errors.specification?.topic}
							aria-describedby={errors.specification?.topic ? "specification-topic-error" : undefined}
							className={`${inputStyle}  w-full h-10`}
							{...register("specification.topic", { setValueAs: (val) => (val === "" ? undefined : val) })}
						/>
						{errors.specification?.topic && (
							<p id="specification-topic-error" className="text-red-500 text-sm mt-1">
								{errors.specification.topic.message}
							</p>
						)}
					</div>

					<div className={divStyle}>
						<label htmlFor="specification-sub-topic" className="font-semibold">
							{t("questionForm.specificationSubTopicLabel")}
						</label>
						<input
							id="specification-sub-topic"
							type="text"
							disabled={isDisabled}
							maxLength={200}
							aria-invalid={!!errors.specification?.subTopic}
							aria-describedby={errors.specification?.subTopic ? "specification-sub-topic-error" : undefined}
							className={`${inputStyle}  w-full h-10`}
							{...register("specification.subTopic", { setValueAs: (val) => (val === "" ? undefined : val) })}
						/>
						{errors.specification?.subTopic && (
							<p id="specification-sub-topic-error" className="text-red-500 text-sm mt-1">
								{errors.specification.subTopic.message}
							</p>
						)}
					</div>
				</fieldset>

				<fieldset className="mt-6 border border-gray-200 rounded-md p-4">
					<legend className={labelStyle}>{t("questionForm.assessmentObjectivesLegend")}</legend>
					<p className="text-sm text-gray-600 mb-2">{t("questionForm.assessmentObjectivesDescription")}</p>

					<div className="flex flex-wrap gap-4">
						{(["ao1", "ao2", "ao3"] as const).map((ao) => (
							<div key={ao} className="flex flex-col">
								<label htmlFor={`assessment-objective-${ao}`} className="font-semibold">
									{t(aoLabelKeys[ao])}
								</label>
								<input
									id={`assessment-objective-${ao}`}
									type="number"
									disabled={isDisabled}
									min={0}
									step={1}
									inputMode="numeric"
									aria-invalid={!!errors.assessmentObjectives?.[ao]}
									aria-describedby={errors.assessmentObjectives?.[ao] ? `assessment-objective-${ao}-error` : undefined}
									className={`${inputStyle} w-32 h-10`}
									{...register(`assessmentObjectives.${ao}`, {
										setValueAs: (val) => (val === "" || val === null ? undefined : Number(val)),
									})}
								/>
								{errors.assessmentObjectives?.[ao] && (
									<p id={`assessment-objective-${ao}-error`} className="text-red-500 text-sm mt-1">
										{errors.assessmentObjectives[ao]?.message}
									</p>
								)}
							</div>
						))}
					</div>
				</fieldset>

				<fieldset className="mt-6 border border-gray-200 rounded-md p-4">
					<legend className={labelStyle}>{t("questionForm.examinerCommentsLabel")}</legend>
					<p className="text-sm text-gray-600 mb-2">{t("questionForm.examinerCommentsDescription")}</p>

					<div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2">
						<div className={fieldStyle}>
							<MathTextField
								id={"examiner-comments-en"}
								label={t("questionForm.examinerCommentsEnLabel")}
								value={watch("en.examinersComments") ?? ""}
								registration={register("en.examinersComments", { setValueAs: (val) => (val === "" ? undefined : val) })}
								textareaClassName={inputStyle}
							/>
							{errors.en?.examinersComments && (
								<p id="examiner-comments-en-error" className="text-red-500 text-sm mt-1">
									{errors.en.examinersComments.message}
								</p>
							)}
						</div>

						<div className={fieldStyle}>
							<MathTextField
								id={"examiner-comments-cy"}
								label={t("questionForm.examinerCommentsCyLabel")}
								value={watch("cy.examinersComments") ?? ""}
								registration={register("cy.examinersComments", { setValueAs: (val) => (val === "" ? undefined : val) })}
								textareaClassName={inputStyle}
							/>

							{errors.cy?.examinersComments && (
								<p id="examiner-comments-cy-error" className="text-red-500 text-sm mt-1">
									{errors.cy.examinersComments.message}
								</p>
							)}
						</div>
					</div>
				</fieldset>

				<fieldset className="mt-6 mb-12 border border-gray-200 rounded-md p-4">
					<legend className={labelStyle}>{t("questionForm.imagesLabel")}</legend>
					<p className="text-sm text-gray-600 mb-4">{t("questionForm.imagesDescription")}</p>

					<div className="grid grid-cols-1 gap-x-6 gap-y-6 lg:grid-cols-2">
						<div className={fieldStyle}>
							<span className="font-semibold">
								{t("questionForm.markSchemeImageLabel")}
								{!isGroupedParent && <RequiredMark />}
							</span>
							<p className="text-sm text-gray-600">{t("questionForm.markSchemeImageHint")}</p>
							<div className="mt-2 flex flex-wrap gap-6">
								<ImageUpload
									label={t("questionForm.imageLanguageEnglish")}
									brand={selectedBrand}
									imageInputId="mark-scheme-image-en"
									existingAssetKey={existingMarkSchemeImageEn ?? undefined}
									onImageUpload={(assetKey) => setValue("en.markSchemeImage", assetKey)}
									onImageRemove={() => setValue("en.markSchemeImage", undefined)}
								/>
								<ImageUpload
									label={`${t("questionForm.imageLanguageWelsh")}${isWelshRequired && !isGroupedParent ? " *" : ""}`}
									brand={selectedBrand}
									imageInputId="mark-scheme-image-cy"
									existingAssetKey={existingMarkSchemeImageCy ?? undefined}
									onImageUpload={(assetKey) => setValue("cy.markSchemeImage", assetKey)}
									onImageRemove={() => setValue("cy.markSchemeImage", undefined)}
								/>
							</div>
						</div>
					</div>
				</fieldset>

				{isStaged && <p className="text-center text-sm text-slate-600">{t("approvalQueue.stagedEditHint")}</p>}
				<div className="flex gap-4 justify-center">
					<button
						type="button"
						onClick={handleCancel}
						disabled={savingStatus !== null}
						className="bg-gray-200 text-gray-700 w-32 h-8 rounded-md font-bold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
					>
						{t("questionForm.cancelButton")}
					</button>
					<button
						type="button"
						onClick={() => void saveWithStatus("draft")}
						disabled={savingStatus !== null || isStaged}
						title={isStaged ? t("approvalQueue.stagedEditHint") : undefined}
						aria-busy={savingStatus === "draft"}
						className="flex items-center justify-center gap-2 bg-white border border-(--admin-color-primary-strong) text-(--admin-color-primary-strong) w-40 h-8 rounded-md font-bold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
					>
						{savingStatus === "draft" ? (
							<>
								<Spinner />
								{t("questionForm.savingButton")}
							</>
						) : (
							t(draftButtonLabelKey)
						)}
					</button>
					<button
						type="submit"
						disabled={savingStatus !== null || isSameUserPublish}
						title={isSameUserPublish ? t("approvalQueue.sameUser") : undefined}
						aria-busy={savingStatus !== null && savingStatus !== "draft"}
						className="flex items-center justify-center gap-2 bg-(--admin-color-primary-strong) text-white w-40 h-8 rounded-md font-bold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
					>
						{savingStatus !== null && savingStatus !== "draft" ? (
							<>
								<Spinner />
								{t("questionForm.savingButton")}
							</>
						) : shouldSaveCurrentStatus ? (
							t("questionForm.saveChangesButton")
						) : isExpert ? (
							t("questionForm.submitButton")
						) : (
							t("approvalQueue.stage")
						)}
					</button>
				</div>
			</div>
		</form>
	);
};
